import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RamalhoLogo } from "@/components/RamalhoLogo";
import {
  ArrowUpRight,
  LineChart,
  ShieldCheck,
  Factory,
  Loader2,
  MessageCircle,
} from "lucide-react";
import { openWhatsapp } from "@/lib/whatsapp";
import { API } from "@/lib/api";

function WhatsappIcon({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function scrollToForm() {
  document.getElementById("cadastro")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const formatCep = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
};
const formatCelular = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export default function Landing() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nome: "",
    cep: "",
    celular: "",
    endereco: "",
    bairro: "",
    cidade: "",
    uf: "",
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleCepBlur = async () => {
    const digits = form.cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${digits}/json/`);
      if (data?.erro) {
        toast.error("CEP não encontrado");
      } else {
        setForm((f) => ({
          ...f,
          endereco: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          uf: data.uf || "",
        }));
      }
    } catch {
      toast.error("Falha ao consultar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.celular.trim()) {
      toast.error("Preencha nome e celular");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        nome: form.nome,
        celular: form.celular,
        cep: form.cep,
        endereco: form.endereco,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf,
      });
    } catch {
      // não bloqueia o WhatsApp mesmo se falhar em salvar
    } finally {
      setSubmitting(false);
    }

    const enderecoStr = [
      form.endereco,
      form.bairro,
      form.cidade && form.uf ? `${form.cidade} - ${form.uf}` : form.cidade || form.uf,
      form.cep,
    ]
      .filter(Boolean)
      .join(", ");

    const lines = [
      "Olá! Tenho interesse em fazer a gestão energética.",
      "",
      `Nome: ${form.nome}`,
      `Celular: ${form.celular}`,
    ];
    if (enderecoStr) lines.push(`Endereço: ${enderecoStr}`);
    openWhatsapp(lines.join("\n"));
    toast.success("Redirecionando para o WhatsApp...");
  };

  const handleQuickWa = () => {
    openWhatsapp("Olá! Tenho interesse em fazer a gestão energética.");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <RamalhoLogo />
          <div className="flex items-center gap-2">
            <button
              onClick={handleQuickWa}
              data-testid="header-whatsapp-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white hover:bg-[#1fb757] h-9 px-4 text-sm font-medium transition-colors"
            >
              <WhatsappIcon />
              Chamar no WhatsApp
            </button>
            <Button
              data-testid="landing-login-btn"
              onClick={() => navigate("/login")}
              variant="outline"
              className="rounded-full border-[#1B4332]/30 text-[#1B4332] hover:bg-[#D8F3DC]/40 px-4 h-9 text-sm"
            >
              Acessar dashboard
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="overline text-[#52796F]">Gestão energética completa</span>
          <h1 className="font-display font-semibold tracking-tight text-5xl md:text-6xl lg:text-7xl text-[#1A1A1A] mt-4 leading-[1.02]">
            Sua usina.
            <br />
            <span className="text-[#1B4332]">Seus créditos.</span>
            <br />
            Sua economia — clara.
          </h1>
          <p className="text-base md:text-lg text-[#4A4A4A] mt-6 max-w-2xl leading-relaxed">
            A Ramalho Gestão Energética entrega a cada cliente uma página exclusiva com a geração da
            usina, o consumo, o saldo de créditos, as tarifas e todos os impostos separadinhos.
          </p>
          <p className="text-sm md:text-base text-[#1B4332] mt-4 max-w-2xl leading-relaxed font-medium">
            A Gestão Energética vai além da geração da energia solar — atua também na eficiência de
            equipamentos e processos industriais e comerciais.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              data-testid="landing-cta-primary"
              onClick={scrollToForm}
              className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22] px-6 h-11"
            >
              Quero começar
            </Button>
            <button
              data-testid="landing-cta-whatsapp"
              onClick={handleQuickWa}
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white hover:bg-[#1fb757] px-6 h-11 text-sm font-medium transition-colors"
            >
              <WhatsappIcon size={18} />
              Chamar no WhatsApp
            </button>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E5DF] bg-white grain-overlay">
            <img
              src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Usina solar em telhado"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
              <div className="text-white/90 text-xs uppercase tracking-[0.2em]">Painel do cliente</div>
              <div className="text-white font-display text-2xl mt-1">Transparência total</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 grid md:grid-cols-3 gap-6">
        {/* Card 1: substituído — Chame no WhatsApp */}
        <div className="relative rounded-lg border border-[#1B4332]/15 bg-gradient-to-br from-[#1B4332] to-[#2d6a4f] text-white p-6 md:p-8 flex flex-col justify-between hover:-translate-y-0.5 transition-transform duration-200">
          <div>
            <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center mb-4">
              <WhatsappIcon size={18} />
            </div>
            <h3 className="font-display text-lg font-semibold">Fale com um especialista</h3>
            <p className="text-sm text-white/85 mt-2 leading-relaxed">
              Faça a gestão da sua usina de energia solar com a Ramalho Gestão Energética.
            </p>
          </div>
          <button
            onClick={handleQuickWa}
            data-testid="feature-whatsapp-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb757] text-white h-10 px-4 text-sm font-medium self-start transition-colors"
          >
            <WhatsappIcon size={16} />
            Chame no WhatsApp
          </button>
        </div>

        {/* Card 2 */}
        <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8 hover:-translate-y-0.5 transition-transform duration-200">
          <div className="h-10 w-10 rounded-full bg-[#D8F3DC] flex items-center justify-center mb-4">
            <LineChart className="h-5 w-5 text-[#1B4332]" />
          </div>
          <h3 className="font-display text-lg font-semibold text-[#1A1A1A]">Geração vs Consumo</h3>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">
            Compare mês a mês o quanto sua usina gerou contra o que você consumiu. Veja seu saldo
            de créditos crescer.
          </p>
        </div>

        {/* Card 3 */}
        <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8 hover:-translate-y-0.5 transition-transform duration-200">
          <div className="h-10 w-10 rounded-full bg-[#D8F3DC] flex items-center justify-center mb-4">
            <Factory className="h-5 w-5 text-[#1B4332]" />
          </div>
          <h3 className="font-display text-lg font-semibold text-[#1A1A1A]">Eficiência industrial</h3>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">
            Vai além da geração solar: análise de equipamentos e processos para reduzir consumo em
            operações industriais e comerciais.
          </p>
        </div>

        {/* Card 4 (span-full linha) */}
        <div className="md:col-span-3 rounded-lg border border-[#E5E5DF] bg-white p-6 md:p-8 hover:-translate-y-0.5 transition-transform duration-200">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-[#D8F3DC] flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-[#1B4332]" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-[#1A1A1A]">Página exclusiva do cliente</h3>
              <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed max-w-3xl">
                Cada cliente tem uma página com sua logomarca, acessada por link único. Créditos,
                saldo, tarifas, impostos e economia — tudo separadinho e fácil de entender.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Formulário de cadastro / primeiro contato */}
      <section
        id="cadastro"
        className="bg-white border-y border-[#E5E5DF]"
        data-testid="lead-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="overline text-[#52796F]">Primeiro contato</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A1A] mt-3 leading-[1.05]">
              Vamos conversar sobre a sua economia?
            </h2>
            <p className="text-base text-[#4A4A4A] mt-5 max-w-lg leading-relaxed">
              Deixe seus dados e você será direcionado direto para o WhatsApp com a Ramalho. Sem
              enrolação, sem robô — atendimento humano.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#1A1A1A]">
              {[
                "Análise gratuita da sua conta de energia",
                "Proposta de economia com dados reais",
                "Acompanhamento mensal de geração e créditos",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#1B4332] flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-[#E5E5DF] bg-[#F9F9F6] p-6 md:p-8 space-y-5"
            data-testid="lead-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="lead-nome" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                Nome
              </Label>
              <Input
                id="lead-nome"
                data-testid="lead-nome-input"
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Seu nome completo"
                className="h-11 bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lead-cep" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                CEP
              </Label>
              <div className="relative">
                <Input
                  id="lead-cep"
                  data-testid="lead-cep-input"
                  value={form.cep}
                  onChange={(e) => setForm({ ...form, cep: formatCep(e.target.value) })}
                  onBlur={handleCepBlur}
                  placeholder="00000-000"
                  inputMode="numeric"
                  className="h-11 bg-white pr-10"
                />
                {cepLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52796F] animate-spin" />
                )}
              </div>
              {(form.endereco || form.cidade) && (
                <p className="text-xs text-[#4A4A4A] mt-1" data-testid="lead-endereco-preview">
                  {[form.endereco, form.bairro, form.cidade && form.uf ? `${form.cidade} - ${form.uf}` : ""]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lead-celular" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                Celular
              </Label>
              <Input
                id="lead-celular"
                data-testid="lead-celular-input"
                required
                value={form.celular}
                onChange={(e) => setForm({ ...form, celular: formatCelular(e.target.value) })}
                placeholder="(00) 00000-0000"
                inputMode="tel"
                className="h-11 bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white h-12 px-6 text-sm font-semibold transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <WhatsappIcon size={18} />
                  Finalizar e chamar no WhatsApp
                </>
              )}
            </button>

            <p className="text-[11px] text-[#4A4A4A] leading-relaxed text-center">
              Ao finalizar, você será redirecionado para o WhatsApp com uma mensagem já pré-preenchida.
            </p>
          </form>
        </div>
      </section>

      <footer className="border-t border-[#E5E5DF] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between text-xs text-[#4A4A4A]">
          <RamalhoLogo size={22} />
          <span>© {new Date().getFullYear()} Ramalho Gestão Energética</span>
        </div>
      </footer>
    </div>
  );
}
